import {useQueryClient} from '@tanstack/react-query'
import {Dialog} from 'primereact/dialog'
import {forwardRef, useImperativeHandle, useMemo, useRef} from 'react'
import {useHistory, useParams} from 'react-router-dom'

import {useSignal} from '@preact/signals-react'
import {useComputed, useSignals} from '@preact/signals-react/runtime'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'

import {Button} from 'primereact/button'
import {InputSwitch} from 'primereact/inputswitch'
import {Tag} from 'primereact/tag'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId, getSuperAdminRole} from '../authentication/AuthUtils'
import {component} from '../form/Constant'
import {useListHospitals} from '../hospital/query-keys/HospitalQueryManager'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {useManagementRoleList} from '../role/query-keys/RoleKeysManager'
import {useUserRoleCRUD} from '../role/query-keys/UserRoleManager'
import StyledHospitalOptions from '../styled-component/styled-options/StyledHospitalOptions'
import StyledRoleOptions from '../styled-component/styled-options/StyledRoleOptions'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    roleAssignFormList,
    roleAssignKeys,
} from './HealthCarePractitionerConstant'
const HCPQueryKey = 'HCP-Detail'

const InitialHospitalFilterRequest = {}
const InitialRoleManagementFilterRequest = {}

export const RoleAssignForm = forwardRef((props, ref) => {
    useSignals()

    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Params
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const history = useHistory()
    //#endregion

    //#region Translate
    const {translateCommons, translateKeys} = TranslateUtils()
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    const {data: hospitalOptions} = useListHospitals(
        user?.id,
        true,
        InitialHospitalFilterRequest,
        !!user?.id,
    )

    const {data: roleOptions} = useManagementRoleList(
        user?.id,
        true,
        InitialRoleManagementFilterRequest,
        !!user?.id,
    )

    const {mutateAsync: createOrUpdateUserRole} = useUserRoleCRUD(
        QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region State
    const data = useSignal({})
    const visible = useSignal(false)
    const formRefs = useRef([])
    const override = useSignal(false)

    //#endregion

    const handleShowForm = value => {
        blockOverflownScroll()
        visible.value = true
        data.value = value && Object.keys(value)?.length > 0 ? {...value} : {}
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        visible.value = false
        data.value = {}
    }

    const handleOnSubmit = async () => {
        if (!data.value[roleAssignKeys.hospitalId]) {
            showNotification(
                severityNotification.error,
                'Please select hospital',
            )
            return
        }

        if (!data.value[roleAssignKeys.roleId]) {
            showNotification(severityNotification.error, 'Please select role')
            return
        }
        if (override.value && !data.value[roleAssignKeys.roleId]) {
            showNotification(
                severityNotification.warn,
                'Please select at least one role',
            )

            return
        }

        let toPostData = {
            userId: id,
            id: data.value?.id || null,
            roleId: data.value[roleAssignKeys.roleId],
            hospitalId: data.value[roleAssignKeys.hospitalId],
            override: data.value[roleAssignKeys.roleId] && override.value,
        }

        const userRoleData = await createOrUpdateUserRole(toPostData)

        if (!!userRoleData?.id) {
            const {userRoleKeys} = QueryKeyManager(
                QueryKeyTypes.userRole,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: userRoleKeys.pattern(),
            })

            handleHideForm()
        }
    }

    const convertedHospitalRoleList = useMemo(() => {
        let tmp = [...roleAssignFormList]
        tmp?.forEach((part, index, theArray) => {
            for (let i = 0; i < part?.children?.length; i++) {
                if (part?.children[i]?.key === roleAssignKeys.roleName) {
                    theArray[index].children[i].options = roleOptions
                        ? roleOptions
                        : []

                    theArray[index].children[i].defaultValue =
                        data.value && data.value?.roleId
                            ? data.value?.roleId
                            : ''

                    if (!roleOptions || roleOptions?.length < 1) {
                        theArray[index].children[0].emptyMessage = (
                            <div>
                                <p>
                                    {translateKeys(
                                        'Currently there is no role has been set up.',
                                    )}
                                    {` `}
                                    <span
                                        className="pointer"
                                        style={{color: 'darkcyan'}}
                                        onClick={() =>
                                            !!getSuperAdminRole() ||
                                            userRole === SystemRole.ADMIN
                                                ? !!getClientId()
                                                    ? history.push(`/clients`)
                                                    : history.push(
                                                          `/clients/${getClientId()}?tv=roleManagement`,
                                                      )
                                                : {}
                                        }>
                                        {!!getSuperAdminRole() ||
                                        userRole === SystemRole.ADMIN
                                            ? translateKeys(
                                                  'Click here to configure',
                                              )
                                            : translateKeys(
                                                  'Please contact your administrator for role configuration',
                                              )}
                                    </span>
                                </p>
                            </div>
                        )
                    } else {
                        delete theArray[index].children[0].emptyMessage
                    }
                }
                if (part?.children[i]?.key === roleAssignKeys.hospitalName) {
                    theArray[index].children[i].options = hospitalOptions
                        ? hospitalOptions
                        : []
                    theArray[index].children[i].defaultValue =
                        data.value && data.value?.hospitalId
                            ? data.value?.hospitalId
                            : ''
                    if (!hospitalOptions || hospitalOptions?.length < 1) {
                        theArray[index].children[0].emptyMessage = (
                            <div>
                                <p>
                                    {/* TODO: Translate */}
                                    Currently there is no hospital has been set
                                    up. {` `}
                                    <span
                                        className="pointer"
                                        style={{color: 'darkcyan'}}
                                        onClick={() =>
                                            !!getSuperAdminRole() ||
                                            userRole === SystemRole.ADMIN
                                                ? history.push(`/hospitals`)
                                                : {}
                                        }>
                                        {!!getSuperAdminRole() ||
                                        userRole === SystemRole.ADMIN
                                            ? translateCommons(
                                                  'Click here to configure',
                                              )
                                            : translateKeys(
                                                  'Please contact your administrator for hospital configuration',
                                              )}
                                    </span>
                                </p>
                            </div>
                        )
                    } else {
                        delete theArray[index].children[0].emptyMessage
                    }
                }
            }
        })
        return tmp
    }, [
        roleOptions,
        data,
        translateKeys,
        userRole,
        history,
        hospitalOptions,
        translateCommons,
    ])

    //#region Summary Hospital
    const getShortens = dataList => {
        if (dataList?.length <= 2) {
            return dataList?.join(', ')
        } else {
            return `${dataList?.slice(0, 2).join(', ')} +${
                dataList?.length - 2
            } more`
        }
    }

    const renderRowItem = (title, subtitle, children) => {
        return (
            <div className="mb-2 p-2">
                <div className="grid p-0" style={{position: 'relative'}}>
                    <div
                        className={
                            !!children
                                ? `md:col-4 col-12 sticky`
                                : 'col-12 sticky'
                        }
                        style={!title ? {top: 0} : {}}>
                        {!!title && (
                            <div className="mb-2">
                                <h6 className="mb-1 underline-60 font-bold p-0 text-sm sm:text-sm md:text-xl lg:text-xl">
                                    {title}
                                </h6>
                            </div>
                        )}
                        {subtitle && typeof subtitle === 'string' ? (
                            <p>{translateCommons(subtitle)}</p>
                        ) : (
                            <div>{subtitle}</div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
    const RoleAssignInformation = useComputed(() => {
        let hospital
        let roles

        if (hospitalOptions?.length && data.value?.hospitalId) {
            hospital = hospitalOptions?.find(
                hos => hos?.value === data.value?.hospitalId,
            )
        }
        if (roleOptions?.length && data.value?.roleId) {
            roles = roleOptions?.find(
                role => role?.value === data.value?.roleId,
            )
        }

        return (
            <div>
                {hospital?.label && (
                    <>
                        You have selected{' '}
                        <span className="text-darkcyan font-bold">
                            {hospital?.label}
                        </span>
                    </>
                )}
                {roles?.label && (
                    <>
                        {' '}
                        as the hospital and the new role of a{' '}
                        <span className="text-darkcyan font-bold">
                            {roles?.label}.
                        </span>{' '}
                    </>
                )}
            </div>
        )
    })

    //#endregion
    return (
        <Dialog
            header={
                <h5 className="mb-0 font-bold">
                    <strong>{translateKeys('Role Assignment Form')}</strong>
                </h5>
            }
            style={{width: '40vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll>
            <div className="grid">
                {convertedHospitalRoleList.map(item => (
                    <div
                        key={`${item.key}`}
                        className="flex flex-column col-12"
                        style={{borderBottom: '1px dashed lightgray'}}>
                        <div className="col-12" style={{position: 'relative'}}>
                            <div className="mb-3">
                                <p className="font-bold p-0 m-0 text-xl md:text-2xl lg:text-2xl">
                                    {translateKeys(
                                        convertKeyToLabel(item?.key),
                                    )}
                                </p>
                                <p className="p-0">{item?.description}</p>
                            </div>
                            {!data.value?.id && (
                                <div className="flex align-items-center justify-content-between py-2 mb-3">
                                    <div>
                                        <h6 className="font-bold">
                                            Override Existing Assignment
                                        </h6>
                                        <p>
                                            This option enables you whether to
                                            override the existing assignment or
                                            not
                                        </p>
                                        <p>
                                            <i className="pi pi-info-circle mr-2"></i>
                                            <span className="font-bold">
                                                {override.value ? (
                                                    <span>
                                                        With override option
                                                        enabled,{' '}
                                                        <span
                                                            style={{
                                                                color: 'red',
                                                            }}>
                                                            any existing
                                                            assignment will be
                                                            deleted
                                                        </span>{' '}
                                                        and replaced with the
                                                        new role.
                                                    </span>
                                                ) : (
                                                    <span>
                                                        With override option
                                                        disabled,{' '}
                                                        <span className="text-darkcyan">
                                                            any existing
                                                            assignment will be
                                                            kept
                                                        </span>{' '}
                                                    </span>
                                                )}
                                            </span>
                                        </p>
                                    </div>
                                    <div>
                                        <InputSwitch
                                            checked={override.value}
                                            onChange={() => {
                                                override.value = !override.value
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {item.children.map(child => (
                                <div key={child?.key} className="col-12 p-0">
                                    <div className="col-12 p-0">
                                        <div className="flex justify-content-between align-items-start">
                                            <h5 className="font-bold p-0 text-sm sm:text-sm md:text-xl lg:text-xl">
                                                {translateKeys(
                                                    convertKeyToLabel(
                                                        child?.key,
                                                    ),
                                                )}
                                            </h5>
                                            <Tag
                                                value={translateCommons(
                                                    'This field is required !',
                                                )}
                                                severity="danger"
                                                icon="pi pi-info-circle"></Tag>
                                        </div>
                                        {child?.key === 'hospitalName' ? (
                                            <div className=" mb-2">
                                                <StyledHospitalOptions
                                                    ref={el =>
                                                        (formRefs.current[
                                                            roleAssignKeys.hospitalId
                                                        ] = el)
                                                    }
                                                    optionType={
                                                        component.dropdownGroup
                                                    }
                                                    groupByIsNull={true}
                                                    placeholder={
                                                        'Please select hospital'
                                                    }
                                                    value={
                                                        data?.value[
                                                            roleAssignKeys
                                                                .hospitalId
                                                        ]
                                                    }
                                                    onChange={newValue => {
                                                        data.value = {
                                                            ...data.value,
                                                            [roleAssignKeys.hospitalId]:
                                                                newValue,
                                                        }
                                                    }}
                                                    disabled={!!data.value?.id}
                                                />
                                            </div>
                                        ) : (
                                            <StyledRoleOptions
                                                ref={el =>
                                                    (formRefs.current[
                                                        roleAssignKeys.roleId
                                                    ] = el)
                                                }
                                                optionType={component.dropdown}
                                                groupByIsNull={true}
                                                placeholder={
                                                    'Please select role'
                                                }
                                                value={
                                                    data?.value[
                                                        roleAssignKeys.roleId
                                                    ]
                                                }
                                                onChange={role => {
                                                    data.value = {
                                                        ...data.value,
                                                        [roleAssignKeys.roleId]:
                                                            role?.value,
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {renderRowItem(
                'Role Assignment Summary',
                RoleAssignInformation,
                null,
            )}
            <div className="px-3">
                <h6 className="font-bold mb-2">{translateCommons('Note')}</h6>
                <ul>
                    <li className="mb-2 ml-2">
                        You can assign to multiple hospitals or organizations.
                        But one hospital or organization, you can only hold one
                        role.
                    </li>
                </ul>
            </div>
            <div className="flex col-12 py-3 px-3">
                <Button
                    className={`col-12 p-button-lg font-bold mr-2 `}
                    icon={'pi pi-send'}
                    label={'SUBMIT'}
                    onClick={handleOnSubmit}
                />
            </div>
        </Dialog>
    )
})
